import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { TaskpanelService } from "src/app/components/direct/service/taskpanel.service";
import { TaskManagementDialogComponent } from "src/app/components/direct/components/task-management-dialog/task-management-dialog.component";

type TaskStatus = "Completed" | "Cancelled" | "Planned";

interface TaskItem {
  name: string;
  type: string;
  startTime: string;
  endTime: string;
  status: TaskStatus;
  statusClass: string;
  iconColorClass: string;

  // for filtering
  assignedTo: string;
  assignedBy: string;
}

interface TaskGroup {
  dateLabel: string;
  tasks: TaskItem[];
}

@Component({
  selector: "app-task-mgmt-side-panel",
  templateUrl: "./task-mgmt-side-panel.component.html",
  styleUrls: ["./task-mgmt-side-panel.component.scss"],
})
export class TaskMgmtSidePanelComponent implements OnInit, OnDestroy {
  drawerOpen = false;
  private sub?: Subscription;

  activeTab: "upcoming" | "assignedToMe" | "assignedByMe" = "upcoming";

  // pretend this is the logged-in user id / username
  // later you can inject a UserService and get it from there
  private currentUserId = "me";

  // master data
  allTaskGroups: TaskGroup[] = [
    {
      dateLabel: "June 16, 2025",
      tasks: [
        {
          name: "Morning Rounds - Ward A",
          type: "Clinical",
          startTime: "05:00AM",
          endTime: "07:00AM",
          status: "Completed",
          statusClass: "task-status--completed",
          iconColorClass: "task-type-icon--blue",
          assignedTo: "me",
          assignedBy: "dr_smith",
        },
        {
          name: "Update Patient B Notes",
          type: "Documentation",
          startTime: "08:00AM",
          endTime: "09:30AM",
          status: "Cancelled",
          statusClass: "task-status--cancelled",
          iconColorClass: "task-type-icon--pink",
          assignedTo: "nurse_jane",
          assignedBy: "me",
        },
        {
          name: "Discharge Summary - Room 304",
          type: "Documentation",
          startTime: "10:00AM",
          endTime: "11:00AM",
          status: "Completed",
          statusClass: "task-status--completed",
          iconColorClass: "task-type-icon--blue",
          assignedTo: "me",
          assignedBy: "dr_smith",
        },
      ],
    },
    {
      dateLabel: "June 17, 2025",
      tasks: [
        {
          name: "Follow up with Patient A",
          type: "Follow-up",
          startTime: "04:00AM",
          endTime: "06:00AM",
          status: "Planned",
          statusClass: "task-status--planned",
          iconColorClass: "task-type-icon--green",
          assignedTo: "me",
          assignedBy: "me",
        },
        {
          name: "Prep for Surgery - Case 21",
          type: "Surgery",
          startTime: "09:00AM",
          endTime: "11:30AM",
          status: "Planned",
          statusClass: "task-status--planned",
          iconColorClass: "task-type-icon--green",
          assignedTo: "dr_smith",
          assignedBy: "me",
        },
      ],
    },
    {
      dateLabel: "June 18, 2025",
      tasks: [
        {
          name: "Team Handover Meeting",
          type: "Meeting",
          startTime: "07:30AM",
          endTime: "08:30AM",
          status: "Planned",
          statusClass: "task-status--planned",
          iconColorClass: "task-type-icon--blue",
          assignedTo: "me",
          assignedBy: "manager",
        },
        {
          name: "Review Lab Results",
          type: "Review",
          startTime: "01:00PM",
          endTime: "02:00PM",
          status: "Completed",
          statusClass: "task-status--completed",
          iconColorClass: "task-type-icon--blue",
          assignedTo: "lab_user",
          assignedBy: "me",
        },
      ],
    },
  ];

  // filtered data for UI
  filteredTaskGroups: TaskGroup[] = [];

  sortAsc: boolean = true;
  showSearchInput: boolean = false;
  searchText: string = "";

  constructor(
    private sidePanelService: TaskpanelService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.sub = this.sidePanelService.isOpen$.subscribe((isOpen) => {
      this.drawerOpen = isOpen;
    });

    // init filtered data based on default tab + empty search
    this.applyFilters();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  closeDrawer() {
    this.sidePanelService.close();
  }

  /* =========================
   * Search / Tabs / Sorting
   * ========================= */

  clearSearch(event: MouseEvent): void {
    event.stopPropagation(); // so it doesn't toggle search visibility
    this.searchText = "";
    this.applyFilters();
  }

  eventSort(): void {
    // sort by startTime asc/desc within each group
    this.sortAsc = !this.sortAsc;

    this.allTaskGroups = this.allTaskGroups.map((group) => ({
      ...group,
      tasks: [...group.tasks].sort((a, b) => {
        const tA = a.startTime;
        const tB = b.startTime;
        return this.sortAsc ? tA.localeCompare(tB) : tB.localeCompare(tA);
      }),
    }));

    this.applyFilters();
  }

  toggleSearchInput(): void {
    this.showSearchInput = !this.showSearchInput;

    if (!this.showSearchInput) {
      this.searchText = "";
    }

    this.applyFilters();
  }

  onSearchTasks(): void {
    this.applyFilters();
  }

  setTab(tab: "upcoming" | "assignedToMe" | "assignedByMe"): void {
    this.activeTab = tab;
    this.applyFilters();
  }

  /** Core filter: applies tab + search on top of allTaskGroups */
  private applyFilters(): void {
    const query = this.searchText.trim().toLowerCase();
    const me = this.currentUserId;

    // 1) clone master list to avoid mutating original
    let groups: TaskGroup[] = this.allTaskGroups.map((g) => ({
      ...g,
      tasks: [...g.tasks],
    }));

    // 2) Tab-based filtering
    if (this.activeTab === "upcoming") {
      // Treat "upcoming" as status === "Planned"
      groups = groups.map((g) => ({
        ...g,
        tasks: g.tasks.filter((t) => t.status === "Planned"),
      }));
    } else if (this.activeTab === "assignedToMe") {
      groups = groups.map((g) => ({
        ...g,
        tasks: g.tasks.filter((t) => t.assignedTo === me),
      }));
    } else if (this.activeTab === "assignedByMe") {
      groups = groups.map((g) => ({
        ...g,
        tasks: g.tasks.filter((t) => t.assignedBy === me),
      }));
    }

    // 3) Search-based filtering
    if (query) {
      groups = groups
        .map((group) => ({
          ...group,
          tasks: group.tasks.filter((task) => {
            const haystack =
              `${task.name} ${task.type} ${task.status}`.toLowerCase();
            return haystack.includes(query);
          }),
        }))
        .filter((group) => group.tasks.length > 0);
    } else {
      // remove empty groups after tab filtering only
      groups = groups.filter((group) => group.tasks.length > 0);
    }

    this.filteredTaskGroups = groups;
  }

  /* =========================
   * Dialog open
   * ========================= */

  openModel(data: TaskItem) {
    const dummyTaskMgmtDetails = {
      id: 101,
      name: "Follow up with Patient A",
      taskType: "patient-followup",
      assignTo: "robert",
      startDate: new Date("2025-01-15"),
      endDate: new Date("2025-01-20"),
      remindMe: true,
      remindInMinutes: 30,
      status: "in-progress",
      priorityLevel: "high",
      tagType: "patient-id",
      tags: [1, 2],
      description: "This task requires a follow up to check progress.",
      attachment: null,
      taskStatus: true,
      isCancelled: false,
    };

    const dialogRef = this.dialog.open(TaskManagementDialogComponent, {
      width: "80vw",
      maxWidth: "700px",
      height: "auto",
      autoFocus: false,
      data: dummyTaskMgmtDetails,
    });

    dialogRef.afterClosed().subscribe(() => {
      // handle dialog result if needed
    });
  }
}
